import { KalturaPurchaseStatus, KalturaEntitlement } from '@kaltura-ott/ott-client-types/api/types';
import { KalturaSubscriptionDependencySet } from '@kaltura-ott/ott-client-types/api/types/KalturaSubscriptionDependencySet';
import {
  subscriptionsGetEntitlements,
  subscriptionsGetAssets,
  subscriptionsGetList,
  subscriptionsGetProductPrices,
  collectionGetAssetList,
  subscriptionsCancel,
  pinValidatePurchase,
  getAssetsByFilter,
  subscriptionsGetSets,
  getHouseholdPaymentMethodList,
  KuxSubscription,
  KuxPackage,
  FormatProductPrice,
  SubscriptionCollection,
  collectionGetBaseAssetList,
  Provider,
} from '@kaltura-ott/tvpil';
import { lastValueFrom } from 'rxjs';

import { cachedPurchaseToPurchaseStatus, checkCachedPurchase } from '../../../entities/entities.service';

export interface SubscriptionItem {
  subscription: KuxSubscription;
  asset?: KuxPackage;
  purchaseInfo: {
    purchaseDate: number;
    isPurchasePending: boolean;
    purchaseStatus?: KalturaPurchaseStatus;
  };
}

export const getSubscriptionEntitlements = async () => {
  const entitlements = await lastValueFrom(subscriptionsGetEntitlements());
  return entitlements.objects;
};

interface GetSubscriptionArgs {
  assetType: string;
  subscriptionIds: string[];
  entitlements: KalturaEntitlement[];
}

export const getSubscriptionsData = async ({
  assetType = '',
  subscriptionIds,
  entitlements,
}: GetSubscriptionArgs): Promise<SubscriptionItem[]> => {
  let subscriptionAssets: KuxPackage[] = [];
  let subscriptions: KuxSubscription[] = [];
  if (subscriptionIds.length > 0) {
    subscriptionAssets = await lastValueFrom(subscriptionsGetAssets(subscriptionIds, assetType));
    subscriptions = await lastValueFrom(subscriptionsGetList(subscriptionIds));
  }
  return subscriptions.map((subscription: KuxSubscription) => {
    const asset = subscriptionAssets.find((asset) => +asset.subscriptionId === +subscription.id);
    const purchase = entitlements?.find(
      (entitlement: KalturaEntitlement) => +entitlement.productId === +subscription.id,
    ) || {
      purchaseDate: 0,
      isPending: false,
    };

    return {
      subscription,
      asset,
      purchaseInfo: {
        purchaseDate: purchase.purchaseDate,
        purchaseStatus: purchase.purchaseDate ? KalturaPurchaseStatus.subscriptionPurchased : undefined,
        isPurchasePending: purchase.isPending,
      },
    };
  });
};

const prepareSubscriptionCollection = async (collectionAssets: KuxPackage[]): Promise<SubscriptionCollection[]> => {
  let productPrices: FormatProductPrice[] = [];
  if (collectionAssets.length > 0) {
    const assetIds = collectionAssets.map((asset) => {
      return asset?.subscriptionId;
    });
    productPrices = await lastValueFrom(subscriptionsGetProductPrices(assetIds));
  }

  const collection = await Promise.all(
    collectionAssets.map(async (subscription) => {
      const productPrice = productPrices.find((price) => +subscription.subscriptionId === +price.productId);
      if (productPrice) {
        const cachedPurchase = await checkCachedPurchase(productPrice, undefined, [
          subscription.subscriptionId.toString(),
        ]);
        const cachedPrice = cachedPurchaseToPurchaseStatus(cachedPurchase, productPrice);
        return {
          subscription: {
            id: subscription.subscriptionId.toString(),
            price: productPrice.price,
            title: subscription.title,
            dependencyType: '',
            externalId: subscription.externalId,
            description: subscription.description,
          },
          assetPackage: subscription,
          purchaseInfo: {
            purchaseStatus: cachedPrice.purchaseStatus || productPrice.purchaseStatus,
          },
        };
      }
    }),
  );

  return collection.filter(Boolean) as SubscriptionCollection[];
};

interface SubscriptionArgs {
  assetType: string;
  collectionId: number;
  basePackageIds: number[];
  subscriptionExcludeIds: string[];
  userCustomerType: string;
  sets?: KalturaSubscriptionDependencySet[];
}

export const getSubscriptionCollection = async ({
  assetType = '',
  collectionId = 0,
  basePackageIds = [],
  subscriptionExcludeIds = [],
  userCustomerType = '',
  sets,
}: SubscriptionArgs): Promise<SubscriptionCollection[]> => {
  const packageSubscriptionIds: string[] = [];
  const packages = sets || (await lastValueFrom(subscriptionsGetSets()));
  packages
    .filter(({ baseSubscriptionId, type }) => basePackageIds.includes(baseSubscriptionId) && type === 'DEPENDENCY')
    .forEach(({ subscriptionIds }) =>
      subscriptionIds.split(',').forEach((id) => {
        if (id) {
          packageSubscriptionIds.push(id);
        }
      }),
    );

  if (!packageSubscriptionIds.length) {
    return [];
  }

  const collectionAssets = await lastValueFrom(
    collectionGetAssetList({
      assetType,
      collectionId,
      packageSubscriptionIds,
      subscriptionExcludeIds,
      userCustomerType,
    }),
  );
  return await prepareSubscriptionCollection(collectionAssets);
};

export const getBaseSubscriptionCollection = async (
  collectionId: number,
  customerType: string,
): Promise<SubscriptionCollection[]> => {
  const collectionAssets = await lastValueFrom(collectionGetBaseAssetList(collectionId, customerType));
  return await prepareSubscriptionCollection(collectionAssets);
};

export const cancelSubscriptions = (subscriptionId: string) => lastValueFrom(subscriptionsCancel(subscriptionId));

export interface PurchaseTransactionProps {
  subscriptionId: string;
  currency: string;
  price: number;
  partnerId: number;
  userType?: string;
  accessToken: string;
  householdExternalId?: string;
}

export const getPaymentMethodList = getHouseholdPaymentMethodList;

export const purchasePinValidate = (pin: string) => lastValueFrom(pinValidatePurchase(pin));

/**
 * Build request payload for service 'Action' action 'List'.
 *
 * Usage: get corresponded package asset for subscription id
 *
 * @category Purchase
 * @function
 */

export const getSubscriptionPackage = async (subscription: KuxSubscription) => {
  const packageResult = await lastValueFrom(getAssetsByFilter({ kSql: `SubscriptionID = '${subscription.id}'` }));
  return packageResult?.objects.length ? packageResult?.objects[0] : undefined;
};

export const addProviderPlanToSubscriptions = (providerData: Provider, provider: string) => {
  if (!provider) {
    return;
  }

  return {
    id: new Date().getTime().toString(),
    title: providerData.title || '',
    price: {
      amount: 0,
      currency: '',
      currencySign: '',
    },
    dependencyType: 'ADDITION_PLAN',
    description: '',
    externalId: '',
    provider: provider,
  };
};

export const isTypePurchased = (purchasedSubscriptionIds: string[], typeSubscriptionIds: string[]): boolean => {
  return !!purchasedSubscriptionIds.find((purchasedSubscriptionId) =>
    typeSubscriptionIds.includes(purchasedSubscriptionId),
  );
};
